import type {
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { useTarget } from "@/components/patterns/SortableList";
import { useState } from "react";
import { AddButton } from "@/components/ui/AddButton";
import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import { useAddClaim } from "@/hooks/use-claim-strategy";
import { useClaimSetWritable } from "@/hooks/use-claim-set-writable";
import { cn } from "@/lib/utils";
import { Refusal } from "./ChainControls";
import { ClaimCard } from "./ClaimCard";
import { ClaimSetDrag, useArranged } from "./ClaimSetDrag";
import { INDEPENDENTS, INTO_SET, underClaim } from "./claim-lanes";
import { ClaimSettingsMenu } from "./ClaimSettingsMenu";
import { ClaimStrategyFooter } from "./ClaimStrategyFooter";
import { HoldingTray } from "./HoldingTray";
import { ClaimSetMoments } from "./claim-set-moments";
import { useTaggingWatch } from "./claim-tagging";
import { DrawnElements, ProvisionalNumerals } from "./drawn-elements";
import { useOpenOnDrawing } from "./open-on-drawing";
import { ScrollRows } from "@/components/ui/ScrollRows";

const NOTE =
  "The positions you defined in the Thesis phase have been used to draft this initial claim set. " +
  "Nothing here is fixed: amend the wording, change a claim's category, remove claims or add new ones however you like.";

/**
 * Every independent claim in one list, in the order the drag is showing,
 * whatever its category, each with its chain beneath it. A position dropped on
 * the list becomes a new independent claim, tagged from its words.
 */
function ArrangedClaims({
  taskId,
  stage,
  editable,
  fold,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
  editable: boolean;
  fold: Folds;
}) {
  const lanes = useArranged(stage);
  const target = useTarget({ id: INTO_SET, accept: "position" });
  const byId = new Map(stage.claims.map((claim) => [claim.claim_id, claim]));
  const pick = (ids: readonly string[] | undefined): ClaimResponse[] =>
    (ids ?? []).flatMap((id) => {
      const claim = byId.get(id);
      return claim === undefined ? [] : [claim];
    });
  return (
    <ul
      ref={editable ? target.ref : undefined}
      data-testid="claim-list"
      className={cn("mt-1", target.chrome)}
    >
      {pick(lanes[INDEPENDENTS]).map((claim, at) => (
        <ClaimCard
          key={claim.claim_id}
          taskId={taskId}
          editable={editable}
          stage={stage}
          claim={claim}
          index={at}
          childrenOf={(claimId) => pick(lanes[underClaim(claimId)])}
          folded={fold.isFolded(claim.claim_id)}
          onFold={(folded) => {
            fold.set(claim.claim_id, folded);
          }}
        />
      ))}
    </ul>
  );
}

/** Which independent claims have their dependents folded away. */
interface Folds {
  isFolded: (claimId: string) => boolean;
  set: (claimId: string, folded: boolean) => void;
  /** Every independent claim folded, or none. */
  setAll: (folded: boolean) => void;
  allFolded: boolean;
}

function useFolds(stage: ClaimStrategyResponse): Folds {
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const independents = stage.claims
    .filter((claim) => claim.parent_claim_id === null)
    .map((claim) => claim.claim_id);
  return {
    isFolded: (claimId) => folded.has(claimId),
    set: (claimId, fold) => {
      setFolded((was) => {
        const next = new Set(was);
        if (fold) next.add(claimId);
        else next.delete(claimId);
        return next;
      });
    },
    setAll: (fold) => {
      setFolded(fold ? new Set(independents) : new Set());
    },
    allFolded:
      independents.length > 0 && independents.every((id) => folded.has(id)),
  };
}

/** How many independent and dependent claims the set holds, as v0.28 says it. */
function countsOf(claims: readonly ClaimResponse[]): string {
  const independent = claims.filter(
    (claim) => claim.kind === "independent",
  ).length;
  return `${String(independent)} independent · ${String(claims.length - independent)} dependent`;
}

/** The line over the claims: their counts, how to read them, and the draft's claim settings at its end. */
function ClaimSetToolbar({
  taskId,
  stage,
  editable,
  fold,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
  editable: boolean;
  fold: Folds;
}) {
  return (
    <div className="mt-3.5 flex flex-wrap items-center gap-3 text-[11.5px]">
      <span className="text-muted-foreground">{countsOf(stage.claims)}</span>
      <Button
        type="button"
        variant="link"
        size="dense"
        onClick={() => {
          fold.setAll(!fold.allFolded);
        }}
      >
        {fold.allFolded
          ? "Show all dependent claims"
          : "Collapse to independent claims"}
      </Button>
      <div className="ml-auto">
        <ClaimSettingsMenu
          taskId={taskId}
          chosen={stage.marker_scheme}
          editable={editable}
        />
      </div>
    </div>
  );
}

/** A new, empty independent claim, which Praxis tags from its words once the attorney writes them. */
function NewIndependent({ taskId }: { taskId: string }) {
  const add = useAddClaim(taskId);
  return (
    <div className="mt-[13px]">
      <AddButton
        emphasis="brand"
        disabled={add.isPending}
        onClick={() => {
          add.mutate({});
        }}
      >
        New independent claim
      </AddButton>
      <Note className="mt-1.5">
        Patent Studio tags the category from the wording. You can change it
        afterwards.
      </Note>
      {add.isError && <Refusal error={add.error} />}
    </div>
  );
}

/** Reads the stage again while Praxis tags a claim; mounted inside the set's moments. */
function TaggingWatch({
  taskId,
  stage,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
}) {
  useTaggingWatch(taskId, stage);
  return null;
}

/** The claim set as served: one ordered list of claims, and the positions not yet placed. */
export function ClaimSet({
  taskId,
  stage,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
}) {
  const editable = useClaimSetWritable(taskId);
  const fold = useFolds(stage);
  const open = useOpenOnDrawing(taskId);
  return (
    <DrawnElements taskId={taskId} open={open}>
      <ClaimSetMoments>
        <TaggingWatch taskId={taskId} stage={stage} />
        <ClaimSetDrag taskId={taskId} stage={stage}>
          <section data-testid="claim-set" className="mt-[26px]">
            <h2 className="text-base leading-[1.3] font-extrabold tracking-[-0.01em]">
              Claim set
            </h2>
            <p className="mt-2 max-w-[78ch] text-detail leading-[1.6] text-muted-foreground">
              {NOTE}
            </p>
            <ProvisionalNumerals claims={stage.claims} />
            <ClaimSetToolbar
              taskId={taskId}
              stage={stage}
              editable={editable}
              fold={fold}
            />
            <ScrollRows label="Claims" data-testid="claim-rows">
              <ArrangedClaims
                taskId={taskId}
                stage={stage}
                editable={editable}
                fold={fold}
              />
            </ScrollRows>
            {editable && <NewIndependent taskId={taskId} />}
            <HoldingTray taskId={taskId} stage={stage} editable={editable} />
            <ClaimStrategyFooter taskId={taskId} stage={stage} />
          </section>
        </ClaimSetDrag>
      </ClaimSetMoments>
    </DrawnElements>
  );
}
